import type { Locale } from "./config";

export interface FeatureItem {
  title: string;
  details: string[];
  /** Labels for the video/media tabs, in order. Omit for cards without tabs. */
  tabLabels?: string[];
}

export interface Review {
  text: string;
  author: string;
  role: string;
}

export interface Dictionary {
  nav: {
    products: string;
    partners: string;
    testimonials: string;
    tryFree: string;
    tryAsStudent: string;
    studentLevels: { k12: string; ielts: string };
    /** Hero primary CTA — longer, more explicit than nav.tryFree. */
    teacherAccount: string;
    freeBadge: string;
    freemiumBadge: string;
    productItems: {
      ieltsGuruCaption: string;
      truongSoCaption: string;
      flyerLmsCaption: string;
      flyerTestPrepCaption: string;
      bingoCaption: string;
    };
  };
  hero: {
    titleRest: string;
    overwhelmed: string;
    descPre: string;
    descHighlight: string;
    descPost: string;
    cta: string;
  };
  features: {
    titleLine1: string;
    titleLine2: string;
    titleHighlight: string;
    tryFree: string;
    items: FeatureItem[];
  };
  partners: {
    heading: string;
    cta: string;
  };
  reviews: {
    heading: string;
    items: Review[];
  };
  footer: {
    company: string;
    address: string[];
    email: string;
    aboutHeading: string;
    aboutLinks: { team: string; blog: string };
    productsHeading: string;
    products: {
      virtualTest: string;
      teado: string;
      bingo: string;
      ielts: string;
    };
    appHeading: string;
    iosLabel: string;
    iosSub: string;
    androidLabel: string;
    androidSub: string;
    rights: string;
  };
  /** Outbound URLs that differ per locale (VI uses the /vi portal). */
  links: {
    teacherHeader: string;
    teacherHero: string;
    /** exam.flyer.us — "try as a student" demo, K-12 branch. */
    studentDemo: string;
    /** ieltsguru.ai — "try as a student" demo, IELTS branch. */
    studentDemoIelts: string;
    teacherFeature: string;
    consultation: string;
    partner: string;
    footerTeado: string;
  };
}

const en: Dictionary = {
  nav: {
    products: "Products",
    partners: "Partners",
    testimonials: "Testimonials",
    tryFree: "Try For Free",
    tryAsStudent: "Try as a Student",
    studentLevels: { k12: "Primary & Secondary", ielts: "IELTS student" },
    teacherAccount: "Get a Free Teacher Account",
    freeBadge: "Free",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "IELTS test preparation platform",
      truongSoCaption: "School operations management platform",
      flyerLmsCaption: "K-6 English LMS",
      flyerTestPrepCaption: "Virtual exam room simulating Cambridge YLE",
      bingoCaption: "AI English tutor for K-6 students",
    },
  },
  hero: {
    titleRest: "Your AI Co‑Teacher for Teaching English",
    overwhelmed: "",
    descPre: "",
    descHighlight: "Save up to 80% of your time",
    descPost: " on lesson planning, homework assignment, grading, and reporting with AI and thousands of ready-to-use teaching resources.",
    cta: "BOOK A CONSULTATION",
  },
  features: {
    titleLine1: "Spend Less Time Preparing.",
    titleLine2: "",
    titleHighlight: "More Time Teaching.",
    tryFree: "Try For Free",
    items: [
      {
        title: "Save time with AI tools",
        details: [
          "▸ AI creates personalized teaching programs based on level and goals.",
          "▸ Quickly generates assignments from PDF, Word, or image files.",
          "▸ AI grades and provides feedback on Speaking and Writing based on the official assessment criteria of international exams.",
        ],
        tabLabels: ["Create programs", "Create exercises", "AI grading"],
      },
      {
        title: "Apply gamification to English teaching",
        details: [
          "▸ Over 6,000 exercises covering various topics and levels from Pre-A1 to C2",
          "▸ Flexible assignments: mock tests, quick practice, minigames, or quizzes.",
          "▸ Gamified exercises with standard American English pronunciation.",
        ],
      },
      {
        title: "Assign exercises & Monitor results with ease",
        details: [
          "▸ Assign homework in only 3 steps. The system automatically grades, saves results, and generates reports for each student or class.",
          "▸ Support class enrollment with entrance tests and automatic student information synchronization.",
          "▸ Help teachers track progress and develop appropriate programs with detailed learning reports.",
        ],
        tabLabels: ["Assign tests", "Entrance tests", "Track progress"],
      },
      {
        title: "High-quality teaching materials",
        details: [
          "▸ Teado is certified by international organizations (EAF and ICEIE) and has received the Smart Education Initiative Certificate from EDTECH Vietnam.",
          "▸ Exercises simulate up to 90% of English proficiency tests: Cambridge, TOEFL, IOE, etc.",
        ],
      },
    ],
  },
  partners: {
    heading: "TRUSTED PARTNERS",
    cta: "BECOME OUR PARTNER!",
  },
  reviews: {
    heading: "WHAT TEACHERS & EDUCATORS ARE SAYING?",
    items: [
      {
        text: "I find that the teachers using FLYER handle everything quite quickly. In just 10 minutes, they can complete all the steps from preparing the questions, assigning homework, and grading the assignments for the students.",
        author: "Ms. Lai Thi Xuan",
        role: "Co-founder LightUp English",
      },
      {
        text: "FLYER is a standardized tool because teachers don't have to rely on intuition to grade or give feedback. They can base assessments on the Cambridge framework, giving objective evaluations.",
        author: "Ms. Trang Phan",
        role: "Top 10 Influential Educators, 2023",
      },
      {
        text: "FLYER already has high-quality tests and great graphics. Now, with the Challenge feature, students are even more motivated and excited to practice.",
        author: "Ms. Loan Lai",
        role: "10 years teaching English at primary level",
      },
      {
        text: "I just need to open a class in the system and assign tests through the app. Grading, feedback, level classification, and storing results are all handled quickly and neatly by FLYER.",
        author: "Ms. Khanh Chi",
        role: "Teacher with 6 years experience",
      },
      {
        text: "I think the platform is fantastic. One of my students had weak English and was shy among peers. After practicing on the platform, they regained confidence and enthusiasm for learning English.",
        author: "Ms. Diem Thuy",
        role: "English Teacher",
      },
    ],
  },
  footer: {
    company: "FLYER TECHNOLOGY JOINT STOCK COMPANY",
    address: ["Address: 23 Young St, Sunshine West", "Melbourne, VIC 3012, Australia"],
    email: "Email: admin@flyer.us",
    aboutHeading: "About us",
    aboutLinks: { team: "Our team", blog: "Blog" },
    productsHeading: "Our Products",
    products: {
      virtualTest: "FLYER - Virtual English Test",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "Download Teado app here:",
    iosLabel: "App Store",
    iosSub: "Download for iOS",
    androidLabel: "Google Play",
    androidSub: "Download for Android",
    rights: "© 2025 FLYER. All rights reserved.",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=header",
    teacherHero:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=en_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=en_ielts",
    teacherFeature:
      "https://teacher.teado.ai/?utm_source=ldp&utm_medium=teacher&utm_campaign=partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_page",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_page",
    footerTeado:
      "https://teacher.teado.ai/?utm_source=ldpteado&utm_medium=link&utm_campaign=footer",
  },
};

const vi: Dictionary = {
  nav: {
    products: "Sản phẩm",
    partners: "Đối tác",
    testimonials: "Đánh giá",
    tryFree: "Trải Nghiệm Miễn Phí",
    tryAsStudent: "Trải Nghiệm Như Học Sinh",
    studentLevels: { k12: "Học sinh tiểu học, THCS", ielts: "Học sinh IELTS" },
    teacherAccount: "Nhận Tài Khoản Giáo Viên Miễn Phí",
    freeBadge: "Miễn phí",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "Giải pháp đào tạo IELTS",
      truongSoCaption: "Giải pháp quản lý vận hành trường học",
      flyerLmsCaption: "Giải pháp quản lý đào tạo tiếng Anh tiểu học",
      flyerTestPrepCaption: "Phòng thi ảo mô phỏng kỳ thi Cambridge YLE",
      bingoCaption: "Gia sư tiếng Anh AI tiểu học",
    },
  },
  hero: {
    titleRest: "Trợ Giảng AI Cho Giáo Viên Tiếng Anh",
    overwhelmed: "",
    descPre: "",
    descHighlight: "Tiết kiệm đến 80% thời gian",
    descPost:
      " cho việc soạn giáo án, tự động giao bài tập, chấm chữa bài và báo cáo bằng AI cùng hàng nghìn học liệu theo chuẩn Cambridge đã được tạo sẵn.",
    cta: "NHẬN TƯ VẤN TẠI ĐÂY",
  },
  features: {
    titleLine1: "Bớt Thời Gian Soạn Bài.",
    titleLine2: "",
    titleHighlight: "Thêm Thời Gian Giảng Dạy.",
    tryFree: "Trải Nghiệm Miễn Phí",
    items: [
      {
        title: "Tiết kiệm thời gian với công cụ AI",
        details: [
          "▸ AI tạo chương trình học cá nhân hóa nhanh theo trình độ và mục tiêu.",
          "▸ Tạo bài tập nhanh từ file PDF, Word hoặc hình ảnh.",
          "▸ AI chấm và nhận xét bài Nói - Viết theo bộ tiêu chí đánh giá chính thức của các kỳ thi quốc tế.",
        ],
        tabLabels: ["Tạo chương trình", "Tạo bài tập", "AI chấm bài"],
      },
      {
        title: "Ứng dụng Game hóa vào giảng dạy tiếng Anh",
        details: [
          "▸ Hơn 6.000 bài tập đa chủ đề và cấp độ từ Pre-A1 đến C2.",
          "▸ Giao bài linh hoạt: thi thử, luyện nhanh, minigame hoặc quiz.",
          "▸ Bài tập game hóa với giọng đọc chuẩn Anh - Mỹ.",
        ],
      },
      {
        title: "Giao bài & Quản lý lớp nhàn tênh",
        details: [
          "▸ Giao bài trong 3 bước, hệ thống tự chấm, lưu kết quả và báo cáo theo từng học sinh hoặc lớp.",
          "▸ Hỗ trợ tuyển sinh cho lớp với kiểm tra đầu vào và tự động đồng bộ thông tin học sinh.",
          "▸ Báo cáo học tập chi tiết giúp giáo viên theo dõi tiến độ và xây dựng chương trình phù hợp.",
        ],
        tabLabels: ["Giao bài", "Kiểm tra đầu vào", "Theo dõi tiến độ"],
      },
      {
        title: "Tài liệu giảng dạy chất lượng cao",
        details: [
          "▸ Chất lượng được kiểm định bởi các tổ chức quốc tế (EAF và ICEIE) và nhận Chứng nhận Sáng kiến Giáo dục Thông minh từ EDTECH Vietnam.",
          "▸ Bài tập mô phỏng chính xác tới 90% theo các kỳ thi chứng chỉ tiếng Anh: Cambridge, TOEFL và chương trình Global Success.",
        ],
      },
    ],
  },
  partners: {
    heading: "CÁC ĐỐI TÁC UY TÍN",
    cta: "HỢP TÁC CÙNG FLYER!",
  },
  reviews: {
    heading: "GIÁO VIÊN NÓI GÌ VỀ TEADO?",
    items: [
      {
        text: "Với FLYER, mình thấy các giáo viên xử lý khá nhanh. Chỉ cần 10 phút, thầy cô có thể làm hết mọi khâu từ soạn đề, giao bài, và chấm chữa cho các con.",
        author: "Cô Lại Thị Xuân",
        role: "Co-founder Trung tâm LightUp English",
      },
      {
        text: "FLYER chính là công cụ chuẩn, vì thầy cô không phải dựa vào cảm tính quá nhiều để chấm bài hay đưa ra nhận xét mà có thể dựa trên nền tảng chuẩn Cambridge, từ đó đưa ra những đánh giá khách quan.",
        author: "Chị Trang Phan",
        role: "Top 10 nhà giáo dục có tầm ảnh hưởng năm 2023",
      },
      {
        text: "FLYER đã có nguồn đề chất lượng và đồ họa đẹp rồi, giờ có thêm tính năng Thách đấu này sẽ giúp học sinh càng hào hứng & có động lực ôn luyện hơn.",
        author: "Cô Loan Lai",
        role: "10 năm kinh nghiệm giảng dạy tiếng Anh cấp 1",
      },
      {
        text: "Chị chỉ cần mở lớp trên hệ thống và giao bài kiểm tra cho học sinh qua ứng dụng. Còn việc chấm điểm, chữa bài và phân loại trình độ, cũng như lưu trữ kết quả của học sinh đã được FLYER xử lý gọn gàng, nhanh chóng.",
        author: "Cô Khánh Chi",
        role: "Giáo viên với 6 năm kinh nghiệm",
      },
      {
        text: "Chị thấy nền tảng luyện thi bên mình rất tuyệt. Bạn học sinh của chị có vốn tiếng Anh kém, và hay xấu hổ với bạn bè. Sau khi ôn luyện trên nền tảng, con đã lấy lại được sự tự tin và hứng thú hơn trong việc học tiếng Anh.",
        author: "Cô Diễm Thúy",
        role: "Giáo viên tiếng Anh",
      },
    ],
  },
  footer: {
    company: "CÔNG TY CỔ PHẦN CÔNG NGHỆ FLYER",
    address: [
      "Trụ sở chính: Số 9 Duy Tân, phường Cầu Giấy, Hà Nội",
      "Chi nhánh TP. Hồ Chí Minh: Số 14 Đường số 3 (Hưng Gia), Khu đô thị Phú Mỹ Hưng",
      "Hotline 1: 0354 635 151 — Hotline 2: 0358 662 975",
    ],
    email: "Email: admin@flyer.us",
    aboutHeading: "Về chúng tôi",
    aboutLinks: { team: "Đội ngũ", blog: "Blog" },
    productsHeading: "Sản phẩm",
    products: {
      virtualTest: "Phòng thi ảo FLYER",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "Tải ứng dụng Teado tại đây:",
    iosLabel: "App Store",
    iosSub: "Dành cho iOS",
    androidLabel: "Google Play",
    androidSub: "Dành cho Android",
    rights: "© 2025 FLYER. All rights reserved.",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/vi/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=header",
    teacherHero:
      "https://teacher.teado.ai/vi/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=vi_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=vi_ielts",
    teacherFeature:
      "https://teacher.teado.ai/vi?utm_source=ldp&utm_medium=teacher&utm_campaign=partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_page",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_page",
    footerTeado:
      "https://www.teado.ai/vi?utm_source=ldpteado&utm_medium=button&utm_campaign=footer",
  },
};

const es: Dictionary = {
  nav: {
    products: "Productos",
    partners: "Aliados",
    testimonials: "Testimonios",
    tryFree: "Prueba gratis",
    tryAsStudent: "Prueba como Estudiante",
    studentLevels: { k12: "Primaria y Secundaria", ielts: "Estudiante de IELTS" },
    teacherAccount: "Obtén una Cuenta Docente Gratis",
    freeBadge: "Gratis",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "Plataforma de preparación para el IELTS",
      truongSoCaption: "Plataforma de gestión escolar",
      flyerLmsCaption: "LMS de inglés para primaria",
      flyerTestPrepCaption: "Sala de examen virtual que simula el Cambridge YLE",
      bingoCaption: "Tutor de inglés con IA para primaria",
    },
  },
  hero: {
    titleRest: "Tu Co‑Docente con IA para Enseñar Inglés",
    overwhelmed: "",
    descPre: "",
    descHighlight: "Ahorra hasta 80% de tu tiempo",
    descPost:
      " en planear clases, asignar tareas, calificar y elaborar reportes, con IA y miles de recursos didácticos listos para usar.",
    cta: "AGENDA UNA ASESORÍA",
  },
  features: {
    titleLine1: "Menos Tiempo Preparando.",
    titleLine2: "",
    titleHighlight: "Más Tiempo Enseñando.",
    tryFree: "Prueba gratis",
    items: [
      {
        title: "Ahorra tiempo con herramientas de IA",
        details: [
          "▸ La IA crea programas de enseñanza personalizados según el nivel y los objetivos.",
          "▸ Genera tareas al instante desde archivos PDF, Word o imágenes.",
          "▸ La IA califica y retroalimenta Speaking y Writing con los criterios oficiales de los exámenes internacionales.",
        ],
        tabLabels: ["Crear programas", "Crear ejercicios", "Calificación con IA"],
      },
      {
        title: "Aplica la gamificación a la enseñanza del inglés",
        details: [
          "▸ Más de 6.000 ejercicios de distintos temas y niveles, de Pre-A1 a C2.",
          "▸ Tareas flexibles: exámenes de práctica, práctica rápida, minijuegos o quizzes.",
          "▸ Ejercicios gamificados con pronunciación estándar del inglés americano.",
        ],
      },
      {
        title: "Asigna tareas y monitorea resultados sin esfuerzo",
        details: [
          "▸ Asigna tareas en solo 3 pasos. El sistema califica automáticamente, guarda los resultados y genera reportes por estudiante o por clase.",
          "▸ Apoya la inscripción a clases con exámenes de admisión y sincronización automática de los datos del estudiante.",
          "▸ Reportes de aprendizaje detallados para seguir el progreso y diseñar el programa adecuado.",
        ],
        tabLabels: ["Asignar exámenes", "Exámenes de admisión", "Seguir el progreso"],
      },
      {
        title: "Material didáctico de alta calidad",
        details: [
          "▸ Teado está certificado por organizaciones internacionales (EAF e ICEIE) y recibió el Certificado de Iniciativa de Educación Inteligente de EDTECH Vietnam.",
          "▸ Los ejercicios simulan hasta el 90% de los exámenes de inglés: Cambridge, TOEFL, IOE, entre otros.",
        ],
      },
    ],
  },
  partners: {
    heading: "ALIADOS DE CONFIANZA",
    cta: "¡CONVIÉRTETE EN NUESTRO ALIADO!",
  },
  reviews: {
    heading: "¿QUÉ DICEN LOS DOCENTES Y EDUCADORES?",
    items: [
      {
        text: "Veo que los docentes que usan FLYER resuelven todo bastante rápido. En solo 10 minutos completan todos los pasos: preparar las preguntas, asignar la tarea y calificar el trabajo de los estudiantes.",
        author: "Prof. Lai Thi Xuan",
        role: "Cofundadora de LightUp English",
      },
      {
        text: "FLYER es una herramienta estandarizada, porque los docentes no tienen que calificar ni retroalimentar por intuición. Pueden basar la evaluación en el marco Cambridge y dar valoraciones objetivas.",
        author: "Prof. Trang Phan",
        role: "Top 10 Educadores Influyentes, 2023",
      },
      {
        text: "FLYER ya tenía exámenes de alta calidad y muy buenos gráficos. Ahora, con la función Challenge, los estudiantes están aún más motivados y entusiasmados por practicar.",
        author: "Prof. Loan Lai",
        role: "10 años enseñando inglés en primaria",
      },
      {
        text: "Solo necesito abrir una clase en el sistema y asignar los exámenes desde la app. La calificación, la retroalimentación, la clasificación por nivel y el registro de resultados los resuelve FLYER de forma rápida y ordenada.",
        author: "Prof. Khanh Chi",
        role: "Docente con 6 años de experiencia",
      },
      {
        text: "Creo que la plataforma es fantástica. Una de mis estudiantes tenía un inglés débil y sentía vergüenza frente a sus compañeros. Después de practicar en la plataforma, recuperó la confianza y el entusiasmo por aprender inglés.",
        author: "Prof. Diem Thuy",
        role: "Docente de inglés",
      },
    ],
  },
  footer: {
    company: "FLYER TECHNOLOGY JOINT STOCK COMPANY",
    address: ["Dirección: 23 Young St, Sunshine West", "Melbourne, VIC 3012, Australia"],
    email: "Email: admin@flyer.us",
    aboutHeading: "Sobre nosotros",
    aboutLinks: { team: "Nuestro equipo", blog: "Blog" },
    productsHeading: "Nuestros productos",
    products: {
      virtualTest: "FLYER - Examen Virtual de Inglés",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "Descarga la app de Teado aquí:",
    iosLabel: "App Store",
    iosSub: "Descargar para iOS",
    androidLabel: "Google Play",
    androidSub: "Descargar para Android",
    rights: "© 2025 FLYER. Todos los derechos reservados.",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=es_header",
    teacherHero:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=es_hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=es_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=es_ielts",
    teacherFeature:
      "https://teacher.teado.ai/?utm_source=ldp&utm_medium=teacher&utm_campaign=es_partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_es",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_es",
    footerTeado:
      "https://teacher.teado.ai/?utm_source=ldpteado&utm_medium=link&utm_campaign=es_footer",
  },
};

const id: Dictionary = {
  nav: {
    products: "Produk",
    partners: "Mitra",
    testimonials: "Testimoni",
    tryFree: "Coba Gratis",
    tryAsStudent: "Coba sebagai Siswa",
    studentLevels: { k12: "Siswa SD & SMP", ielts: "Siswa IELTS" },
    teacherAccount: "Dapatkan Akun Guru Gratis",
    freeBadge: "Gratis",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "Platform persiapan tes IELTS",
      truongSoCaption: "Platform manajemen operasional sekolah",
      flyerLmsCaption: "LMS Bahasa Inggris SD (K-6)",
      flyerTestPrepCaption: "Ruang ujian virtual simulasi Cambridge YLE",
      bingoCaption: "Tutor AI Bahasa Inggris untuk siswa SD",
    },
  },
  hero: {
    titleRest: "Co‑Teacher AI Anda untuk Mengajar Bahasa Inggris",
    overwhelmed: "",
    descPre: "",
    descHighlight: "Hemat hingga 80% waktu Anda",
    descPost:
      " untuk menyusun rencana pembelajaran, memberi tugas, menilai, dan membuat laporan dengan AI serta ribuan materi ajar siap pakai.",
    cta: "JADWALKAN KONSULTASI",
  },
  features: {
    titleLine1: "Kurangi Waktu Persiapan.",
    titleLine2: "",
    titleHighlight: "Perbanyak Waktu Mengajar.",
    tryFree: "Coba Gratis",
    items: [
      {
        title: "Hemat waktu dengan perangkat AI",
        details: [
          "▸ AI menyusun program pengajaran yang dipersonalisasi sesuai level dan tujuan.",
          "▸ Membuat soal dengan cepat dari file PDF, Word, atau gambar.",
          "▸ AI menilai dan memberi umpan balik untuk Speaking dan Writing berdasarkan kriteria penilaian resmi ujian internasional.",
        ],
        tabLabels: ["Buat program", "Buat latihan", "Penilaian AI"],
      },
      {
        title: "Terapkan gamifikasi dalam pengajaran Bahasa Inggris",
        details: [
          "▸ Lebih dari 6.000 latihan mencakup beragam topik dan level dari Pre-A1 hingga C2",
          "▸ Penugasan fleksibel: tes simulasi, latihan cepat, minigame, atau kuis.",
          "▸ Latihan bergaya permainan dengan pelafalan Bahasa Inggris Amerika standar.",
        ],
      },
      {
        title: "Beri tugas & pantau hasil dengan mudah",
        details: [
          "▸ Beri tugas hanya dalam 3 langkah. Sistem otomatis menilai, menyimpan hasil, dan membuat laporan per siswa atau per kelas.",
          "▸ Mendukung penerimaan siswa dengan tes masuk dan sinkronisasi data siswa otomatis.",
          "▸ Membantu guru memantau kemajuan dan menyusun program yang tepat melalui laporan belajar terperinci.",
        ],
        tabLabels: ["Beri tugas", "Tes masuk", "Pantau kemajuan"],
      },
      {
        title: "Materi ajar berkualitas tinggi",
        details: [
          "▸ Teado tersertifikasi oleh organisasi internasional (EAF dan ICEIE) dan menerima Sertifikat Smart Education Initiative dari EDTECH Vietnam.",
          "▸ Latihan menyimulasikan hingga 90% tes kemampuan Bahasa Inggris: Cambridge, TOEFL, IOE, dll.",
        ],
      },
    ],
  },
  partners: {
    heading: "MITRA TEPERCAYA",
    cta: "JADILAH MITRA KAMI!",
  },
  reviews: {
    heading: "APA KATA PARA GURU & PENDIDIK?",
    items: [],
  },
  footer: {
    company: "FLYER TECHNOLOGY JOINT STOCK COMPANY",
    address: ["Alamat: 23 Young St, Sunshine West", "Melbourne, VIC 3012, Australia"],
    email: "Email: admin@flyer.us",
    aboutHeading: "Tentang kami",
    aboutLinks: { team: "Tim kami", blog: "Blog" },
    productsHeading: "Produk Kami",
    products: {
      virtualTest: "FLYER - Tes Bahasa Inggris Virtual",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "Unduh aplikasi Teado di sini:",
    iosLabel: "App Store",
    iosSub: "Unduh untuk iOS",
    androidLabel: "Google Play",
    androidSub: "Unduh untuk Android",
    rights: "© 2025 FLYER. Seluruh hak cipta dilindungi.",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=id_header",
    teacherHero:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=id_hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=id_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=id_ielts",
    teacherFeature:
      "https://teacher.teado.ai/?utm_source=ldp&utm_medium=teacher&utm_campaign=id_partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_id",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_id",
    footerTeado:
      "https://teacher.teado.ai/?utm_source=ldpteado&utm_medium=link&utm_campaign=id_footer",
  },
};

const th: Dictionary = {
  nav: {
    products: "ผลิตภัณฑ์",
    partners: "พันธมิตร",
    testimonials: "รีวิว",
    tryFree: "ทดลองใช้ฟรี",
    tryAsStudent: "ทดลองเป็นนักเรียน",
    studentLevels: { k12: "นักเรียนประถม–มัธยมต้น", ielts: "นักเรียน IELTS" },
    teacherAccount: "รับบัญชีครูฟรี",
    freeBadge: "ฟรี",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "แพลตฟอร์มเตรียมสอบ IELTS",
      truongSoCaption: "แพลตฟอร์มบริหารจัดการโรงเรียน",
      flyerLmsCaption: "LMS ภาษาอังกฤษระดับประถม (K-6)",
      flyerTestPrepCaption: "ห้องสอบเสมือนจริงจำลองการสอบ Cambridge YLE",
      bingoCaption: "ติวเตอร์ AI ภาษาอังกฤษสำหรับนักเรียนประถม",
    },
  },
  hero: {
    titleRest: "ผู้ช่วยสอน AI ของคุณสำหรับการสอนภาษาอังกฤษ",
    overwhelmed: "",
    descPre: "",
    descHighlight: "ประหยัดเวลาได้ถึง 80%",
    descPost:
      " ในการเตรียมแผนการสอน มอบหมายการบ้าน ตรวจงาน และทำรายงาน ด้วย AI พร้อมสื่อการสอนสำเร็จรูปหลายพันรายการ",
    cta: "นัดหมายรับคำปรึกษา",
  },
  features: {
    titleLine1: "ลดเวลาเตรียมสอน",
    titleLine2: "",
    titleHighlight: "เพิ่มเวลาสอนจริง",
    tryFree: "ทดลองใช้ฟรี",
    items: [
      {
        title: "ประหยัดเวลาด้วยเครื่องมือ AI",
        details: [
          "▸ AI สร้างโปรแกรมการสอนเฉพาะบุคคลตามระดับและเป้าหมาย",
          "▸ สร้างแบบฝึกหัดอย่างรวดเร็วจากไฟล์ PDF, Word หรือรูปภาพ",
          "▸ AI ตรวจและให้ฟีดแบ็กทักษะ Speaking และ Writing ตามเกณฑ์การประเมินอย่างเป็นทางการของข้อสอบระดับสากล",
        ],
        tabLabels: ["สร้างโปรแกรม", "สร้างแบบฝึกหัด", "AI ตรวจงาน"],
      },
      {
        title: "ใช้เกมมิฟิเคชันในการสอนภาษาอังกฤษ",
        details: [
          "▸ แบบฝึกหัดกว่า 6,000 รายการ ครอบคลุมหลากหลายหัวข้อและระดับ ตั้งแต่ Pre-A1 ถึง C2",
          "▸ มอบหมายงานได้ยืดหยุ่น: ข้อสอบเสมือนจริง ฝึกฝนเร็ว มินิเกม หรือควิซ",
          "▸ แบบฝึกหัดสไตล์เกมพร้อมการออกเสียงภาษาอังกฤษแบบอเมริกันมาตรฐาน",
        ],
      },
      {
        title: "มอบหมายงาน & ติดตามผลได้ง่าย",
        details: [
          "▸ มอบหมายการบ้านเพียง 3 ขั้นตอน ระบบตรวจอัตโนมัติ บันทึกผล และสร้างรายงานรายนักเรียนหรือรายห้องเรียน",
          "▸ รองรับการรับนักเรียนด้วยข้อสอบวัดระดับ พร้อมซิงก์ข้อมูลนักเรียนอัตโนมัติ",
          "▸ ช่วยครูติดตามพัฒนาการและวางโปรแกรมที่เหมาะสมด้วยรายงานการเรียนโดยละเอียด",
        ],
        tabLabels: ["มอบหมายงาน", "ข้อสอบวัดระดับ", "ติดตามพัฒนาการ"],
      },
      {
        title: "สื่อการสอนคุณภาพสูง",
        details: [
          "▸ Teado ได้รับการรับรองจากองค์กรระดับนานาชาติ (EAF และ ICEIE) และได้รับ Smart Education Initiative Certificate จาก EDTECH Vietnam",
          "▸ แบบฝึกหัดจำลองข้อสอบวัดระดับภาษาอังกฤษได้ถึง 90%: Cambridge, TOEFL, IOE ฯลฯ",
        ],
      },
    ],
  },
  partners: {
    heading: "พันธมิตรที่ไว้วางใจเรา",
    cta: "ร่วมเป็นพันธมิตรกับเรา!",
  },
  reviews: {
    heading: "เสียงจากครูและนักการศึกษา",
    items: [],
  },
  footer: {
    company: "FLYER TECHNOLOGY JOINT STOCK COMPANY",
    address: ["ที่อยู่: 23 Young St, Sunshine West", "Melbourne, VIC 3012, Australia"],
    email: "อีเมล: admin@flyer.us",
    aboutHeading: "เกี่ยวกับเรา",
    aboutLinks: { team: "ทีมของเรา", blog: "บล็อก" },
    productsHeading: "ผลิตภัณฑ์ของเรา",
    products: {
      virtualTest: "FLYER - ห้องสอบภาษาอังกฤษเสมือนจริง",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "ดาวน์โหลดแอป Teado ได้ที่นี่:",
    iosLabel: "App Store",
    iosSub: "ดาวน์โหลดสำหรับ iOS",
    androidLabel: "Google Play",
    androidSub: "ดาวน์โหลดสำหรับ Android",
    rights: "© 2025 FLYER. สงวนลิขสิทธิ์",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=th_header",
    teacherHero:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=th_hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=th_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=th_ielts",
    teacherFeature:
      "https://teacher.teado.ai/?utm_source=ldp&utm_medium=teacher&utm_campaign=th_partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_th",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_th",
    footerTeado:
      "https://teacher.teado.ai/?utm_source=ldpteado&utm_medium=link&utm_campaign=th_footer",
  },
};

const lo: Dictionary = {
  nav: {
    products: "ຜະລິດຕະພັນ",
    partners: "ຄູ່ຮ່ວມມື",
    testimonials: "ຄຳຄິດເຫັນ",
    tryFree: "ທົດລອງໃຊ້ຟຣີ",
    tryAsStudent: "ທົດລອງເປັນນັກຮຽນ",
    studentLevels: { k12: "ນັກຮຽນປະຖົມ–ມັດທະຍົມຕົ້ນ", ielts: "ນັກຮຽນ IELTS" },
    teacherAccount: "ຮັບບັນຊີຄູຟຣີ",
    freeBadge: "ຟຣີ",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "ແພລດຟອມກຽມສອບ IELTS",
      truongSoCaption: "ແພລດຟອມບໍລິຫານຈັດການໂຮງຮຽນ",
      flyerLmsCaption: "LMS ພາສາອັງກິດຊັ້ນປະຖົມ (K-6)",
      flyerTestPrepCaption: "ຫ້ອງສອບເສີມຈຳລອງການສອບ Cambridge YLE",
      bingoCaption: "ຄູສອນ AI ພາສາອັງກິດສຳລັບນັກຮຽນປະຖົມ",
    },
  },
  hero: {
    titleRest: "ຜູ້ຊ່ວຍສອນ AI ຂອງທ່ານສຳລັບການສອນພາສາອັງກິດ",
    overwhelmed: "",
    descPre: "",
    descHighlight: "ປະຢັດເວລາໄດ້ເຖິງ 80%",
    descPost:
      " ໃນການກຽມແຜນການສອນ ມອບໝາຍວຽກບ້ານ ກວດວຽກ ແລະ ເຮັດບົດລາຍງານ ດ້ວຍ AI ພ້ອມສື່ການສອນສຳເລັດຮູບຫຼາຍພັນລາຍການ",
    cta: "ນັດໝາຍຮັບຄຳປຶກສາ",
  },
  features: {
    titleLine1: "ຫຼຸດເວລາກຽມສອນ.",
    titleLine2: "",
    titleHighlight: "ເພີ່ມເວລາສອນແທ້.",
    tryFree: "ທົດລອງໃຊ້ຟຣີ",
    items: [
      {
        title: "ປະຢັດເວລາດ້ວຍເຄື່ອງມື AI",
        details: [
          "▸ AI ສ້າງໂປຣແກຣມການສອນສະເພາະບຸກຄົນຕາມລະດັບ ແລະ ເປົ້າໝາຍ",
          "▸ ສ້າງບົດຝຶກຫັດຢ່າງວ່ອງໄວຈາກໄຟລ໌ PDF, Word ຫຼື ຮູບພາບ",
          "▸ AI ກວດ ແລະ ໃຫ້ຄຳຕິຊົມທັກສະ Speaking ແລະ Writing ຕາມເກນການປະເມີນທາງການຂອງການສອບລະດັບສາກົນ",
        ],
        tabLabels: ["ສ້າງໂປຣແກຣມ", "ສ້າງບົດຝຶກຫັດ", "AI ກວດວຽກ"],
      },
      {
        title: "ນຳໃຊ້ເກມມິຟິເຄຊັນໃນການສອນພາສາອັງກິດ",
        details: [
          "▸ ບົດຝຶກຫັດຫຼາຍກວ່າ 6,000 ລາຍການ ກວມເອົາຫຼາຍຫົວຂໍ້ ແລະ ລະດັບ ຕັ້ງແຕ່ Pre-A1 ຫາ C2",
          "▸ ມອບໝາຍວຽກໄດ້ຢືດຢຸ່ນ: ສອບເສີມຈຳລອງ ຝຶກໄວ ມິນິເກມ ຫຼື ຄວິສ",
          "▸ ບົດຝຶກຫັດແບບເກມພ້ອມການອອກສຽງພາສາອັງກິດແບບອາເມຣິກັນມາດຕະຖານ",
        ],
      },
      {
        title: "ມອບໝາຍວຽກ & ຕິດຕາມຜົນໄດ້ງ່າຍ",
        details: [
          "▸ ມອບໝາຍວຽກບ້ານພຽງ 3 ຂັ້ນຕອນ ລະບົບກວດອັດຕະໂນມັດ ບັນທຶກຜົນ ແລະ ສ້າງບົດລາຍງານລາຍນັກຮຽນ ຫຼື ລາຍຫ້ອງຮຽນ",
          "▸ ຮອງຮັບການຮັບນັກຮຽນດ້ວຍການສອບວັດລະດັບ ພ້ອມຊິງຂໍ້ມູນນັກຮຽນອັດຕະໂນມັດ",
          "▸ ຊ່ວຍຄູຕິດຕາມພັດທະນາການ ແລະ ວາງໂປຣແກຣມທີ່ເໝາະສົມດ້ວຍບົດລາຍງານການຮຽນລະອຽດ",
        ],
        tabLabels: ["ມອບໝາຍວຽກ", "ສອບວັດລະດັບ", "ຕິດຕາມພັດທະນາການ"],
      },
      {
        title: "ສື່ການສອນຄຸນນະພາບສູງ",
        details: [
          "▸ Teado ໄດ້ຮັບການຢັ້ງຢືນຈາກອົງກອນລະດັບສາກົນ (EAF ແລະ ICEIE) ແລະ ໄດ້ຮັບ Smart Education Initiative Certificate ຈາກ EDTECH Vietnam",
          "▸ ບົດຝຶກຫັດຈຳລອງການສອບວັດລະດັບພາສາອັງກິດໄດ້ເຖິງ 90%: Cambridge, TOEFL, IOE ແລະ ອື່ນໆ",
        ],
      },
    ],
  },
  partners: {
    heading: "ຄູ່ຮ່ວມມືທີ່ໄວ້ວາງໃຈ",
    cta: "ຮ່ວມເປັນຄູ່ຮ່ວມມືກັບພວກເຮົາ!",
  },
  reviews: {
    heading: "ສຽງຈາກຄູ ແລະ ນັກການສຶກສາ",
    items: [],
  },
  footer: {
    company: "FLYER TECHNOLOGY JOINT STOCK COMPANY",
    address: ["ທີ່ຢູ່: 23 Young St, Sunshine West", "Melbourne, VIC 3012, Australia"],
    email: "ອີເມວ: admin@flyer.us",
    aboutHeading: "ກ່ຽວກັບພວກເຮົາ",
    aboutLinks: { team: "ທີມຂອງພວກເຮົາ", blog: "ບລັອກ" },
    productsHeading: "ຜະລິດຕະພັນຂອງພວກເຮົາ",
    products: {
      virtualTest: "FLYER - ຫ້ອງສອບພາສາອັງກິດເສີມ",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "ດາວໂຫຼດແອັບ Teado ໄດ້ທີ່ນີ້:",
    iosLabel: "App Store",
    iosSub: "ດາວໂຫຼດສຳລັບ iOS",
    androidLabel: "Google Play",
    androidSub: "ດາວໂຫຼດສຳລັບ Android",
    rights: "© 2025 FLYER. ສະຫງວນລິຂະສິດ",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=lo_header",
    teacherHero:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=lo_hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=lo_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=lo_ielts",
    teacherFeature:
      "https://teacher.teado.ai/?utm_source=ldp&utm_medium=teacher&utm_campaign=lo_partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_lo",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_lo",
    footerTeado:
      "https://teacher.teado.ai/?utm_source=ldpteado&utm_medium=link&utm_campaign=lo_footer",
  },
};

const pt: Dictionary = {
  nav: {
    products: "Produtos",
    partners: "Parceiros",
    testimonials: "Depoimentos",
    tryFree: "Experimente Grátis",
    tryAsStudent: "Experimente como Aluno",
    studentLevels: { k12: "Fundamental", ielts: "Aluno de IELTS" },
    teacherAccount: "Ganhe uma Conta de Professor Grátis",
    freeBadge: "Grátis",
    freemiumBadge: "Freemium",
    productItems: {
      ieltsGuruCaption: "Plataforma de preparação para o IELTS",
      truongSoCaption: "Plataforma de gestão escolar",
      flyerLmsCaption: "LMS de inglês para o ensino fundamental (K-6)",
      flyerTestPrepCaption: "Sala de exame virtual que simula o Cambridge YLE",
      bingoCaption: "Tutor de inglês com IA para alunos do fundamental",
    },
  },
  hero: {
    titleRest: "Seu Co‑Teacher de IA para Ensinar Inglês",
    overwhelmed: "",
    descPre: "",
    descHighlight: "Economize até 80% do seu tempo",
    descPost:
      " no planejamento de aulas, envio de tarefas, correção e relatórios com IA e milhares de recursos didáticos prontos para usar.",
    cta: "AGENDE UMA CONSULTORIA",
  },
  features: {
    titleLine1: "Menos Tempo Preparando.",
    titleLine2: "",
    titleHighlight: "Mais Tempo Ensinando.",
    tryFree: "Experimente Grátis",
    items: [
      {
        title: "Economize tempo com ferramentas de IA",
        details: [
          "▸ A IA cria programas de ensino personalizados conforme o nível e os objetivos.",
          "▸ Gere exercícios rapidamente a partir de arquivos PDF, Word ou imagens.",
          "▸ A IA corrige e dá feedback de Speaking e Writing com base nos critérios oficiais de avaliação dos exames internacionais.",
        ],
        tabLabels: ["Criar programas", "Criar exercícios", "Correção por IA"],
      },
      {
        title: "Aplique gamificação ao ensino de inglês",
        details: [
          "▸ Mais de 6.000 exercícios cobrindo diversos temas e níveis, do Pre-A1 ao C2",
          "▸ Tarefas flexíveis: simulados, prática rápida, minigames ou quizzes.",
          "▸ Exercícios gamificados com pronúncia padrão do inglês americano.",
        ],
      },
      {
        title: "Envie tarefas e acompanhe resultados com facilidade",
        details: [
          "▸ Envie tarefas em apenas 3 passos. O sistema corrige automaticamente, salva os resultados e gera relatórios por aluno ou por turma.",
          "▸ Suporte à matrícula com testes de nivelamento e sincronização automática dos dados dos alunos.",
          "▸ Ajude os professores a acompanhar o progresso e montar programas adequados com relatórios de aprendizagem detalhados.",
        ],
        tabLabels: ["Enviar tarefas", "Teste de nivelamento", "Acompanhar progresso"],
      },
      {
        title: "Materiais didáticos de alta qualidade",
        details: [
          "▸ O Teado é certificado por organizações internacionais (EAF e ICEIE) e recebeu o Certificado Smart Education Initiative da EDTECH Vietnam.",
          "▸ Os exercícios simulam até 90% dos testes de proficiência em inglês: Cambridge, TOEFL, IOE, etc.",
        ],
      },
    ],
  },
  partners: {
    heading: "PARCEIROS DE CONFIANÇA",
    cta: "SEJA NOSSO PARCEIRO!",
  },
  reviews: {
    heading: "O QUE DIZEM PROFESSORES E EDUCADORES?",
    items: [],
  },
  footer: {
    company: "FLYER TECHNOLOGY JOINT STOCK COMPANY",
    address: ["Endereço: 23 Young St, Sunshine West", "Melbourne, VIC 3012, Austrália"],
    email: "E-mail: admin@flyer.us",
    aboutHeading: "Sobre nós",
    aboutLinks: { team: "Nossa equipe", blog: "Blog" },
    productsHeading: "Nossos Produtos",
    products: {
      virtualTest: "FLYER - Teste Virtual de Inglês",
      teado: "Teado",
      bingo: "Bingo AI",
      ielts: "IELTS Guru",
    },
    appHeading: "Baixe o aplicativo Teado aqui:",
    iosLabel: "App Store",
    iosSub: "Baixar para iOS",
    androidLabel: "Google Play",
    androidSub: "Baixar para Android",
    rights: "© 2025 FLYER. Todos os direitos reservados.",
  },
  links: {
    teacherHeader:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=pt_header",
    teacherHero:
      "https://teacher.teado.ai/signup?utm_source=ldpteado&utm_medium=button&utm_campaign=pt_hero",
    studentDemo:
      "https://exam.flyer.us/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=pt_k12",
    studentDemoIelts:
      "https://ieltsguru.ai/?utm_source=ldpteado&utm_medium=button&utm_campaign=hero_student&utm_content=pt_ielts",
    teacherFeature:
      "https://teacher.teado.ai/?utm_source=ldp&utm_medium=teacher&utm_campaign=pt_partner",
    consultation:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_pt",
    partner:
      "https://tally.so/r/jabYVR?utm_source=B2B&utm_medium=button&utm_campaign=teado_landing_pt",
    footerTeado:
      "https://teacher.teado.ai/?utm_source=ldpteado&utm_medium=link&utm_campaign=pt_footer",
  },
};

export const dictionaries: Record<Locale, Dictionary> = {
  en,
  vi,
  es,
  id,
  th,
  lo,
  pt,
};
